<template>
    <CDropdown ref="dropdown" class="CFormSearchSelect w-full" @open="onOpen">
        <template #trigger="{ isOpen }">
            <CFormInput
                v-bind="$attrs"
                class="cursor-pointer"
                :class="{ 'font-medium': selectedItemsLabel }"
                readonly
                :suffix-icon="isOpen ? 'dropdown-up' : 'dropdown-down'"
                :value="selectedItemsLabel"
                @keydown.esc.prevent="dropdown?.close()"
                @keypress.enter.prevent="dropdown?.open()"
                @keypress.space.prevent="dropdown?.open()"
            />
        </template>

        <template #content>
            <slot name="dropdown-header" />

            <div class="flex min-w-64 flex-col gap-4">
                <CFormInput v-model="searchQuery" class="w-full" placeholder="Vyhľadať…" prefix-icon="search">
                    <template v-if="searchQuery" #suffix>
                        <CIcon class="hover:100 size-6 cursor-pointer opacity-75" name="close" @click="clear" />
                    </template>
                </CFormInput>

                <div v-if="filteredOptions.length" ref="container" class="max-h-96 overflow-y-auto">
                    <CFormRadio
                        :model-value="modelValue"
                        option-disabled-key="disabled"
                        option-label-key="label"
                        option-value-key="value"
                        :options="filteredOptions"
                        @update:model-value="onSelect"
                    />
                </div>
            </div>

            <slot name="dropdown-footer" />
        </template>
    </CDropdown>
</template>

<script setup lang="ts">
import type { Extractor } from '@/utils/normalizeOption'
import { normalizeOptions } from '@/utils/normalizeOption'
import { useDebounceFn } from '@vueuse/core'
import { computed, nextTick, ref, watch } from 'vue'

interface Props {
    modelValue?: string | number
    options?: any[] | Record<PropertyKey, any>
    labelKey?: Extractor
    valueKey?: Extractor
    disabledKey?: Extractor
}

const props = withDefaults(defineProps<Props>(), {
    modelValue: undefined,
    options: () => [],
    labelKey: undefined,
    valueKey: undefined,
    disabledKey: undefined
})

const emit = defineEmits<{
    'update:model-value': [value: string | number]
}>()

const dropdown = ref()
const container = ref()
const searchQuery = ref('')
const debouncedQuery = ref('')

const updateDebouncedQuery = useDebounceFn((value: string) => {
    debouncedQuery.value = value.toLowerCase()
}, 300)

watch(searchQuery, (value) => {
    updateDebouncedQuery(value)
})

const normalizedOptions = computed(() => {
    const normalized = normalizeOptions(props.options, {
        value: props.valueKey,
        label: props.labelKey,
        disabled: props.disabledKey
    })
    return normalized || []
})

const filteredOptions = computed(() => {
    if (!debouncedQuery.value) return normalizedOptions.value

    return normalizedOptions.value.filter((option) => option.label.toLowerCase().includes(debouncedQuery.value))
})

const selectedItemsLabel = computed(() => {
    const value = props.modelValue
    if (!value) return ''

    const selectedOption = normalizedOptions.value.find((option) => {
        // Handle both strict equality and string comparison
        return option.value === value || String(option.value) === String(value)
    })
    if (!selectedOption) return ''

    return selectedOption.label
})

const clear = () => {
    searchQuery.value = ''
}

const onSelect = (value: string | number) => {
    console.log('CFormSearchSelect onSelect called with:', value)
    emit('update:model-value', value)
    dropdown.value?.close()
}

const onOpen = () => {
    if (!props.modelValue) return

    nextTick(() => {
        const containerElement = container.value instanceof HTMLElement ? container.value : null
        if (!containerElement) return

        const normalizedValue =
            typeof CSS !== 'undefined' && typeof CSS.escape === 'function'
                ? CSS.escape(String(props.modelValue))
                : String(props.modelValue)

        const selectedRadio = containerElement.querySelector(`input[value="${normalizedValue}"]`)
        if (!(selectedRadio instanceof HTMLElement)) return

        const offset = selectedRadio.getBoundingClientRect().top - containerElement.getBoundingClientRect().top
        containerElement.scrollTop += offset
    })
}

defineOptions({
    inheritAttrs: false
})
</script>
